import { Box, Button, Container, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import OneSignal from "react-onesignal";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { setAppleTouchIcon, setFavicon } from "utils/icon";
import { addManifest } from "utils/manifest";

import { handleErrors } from "helpers/errors";
import { stores } from "helpers/stores";

import { useCurrentStore } from "hooks/storeHooks";
import { usePromptInstall } from "hooks/usePromptInstall";
import { useIsAuthenticated, useUserAuthenticatedStoreHash } from "hooks/userHooks";

import { getStoreConfig } from "services/api";

import { StoreState } from "states/StoreState";
import { UserState } from "states/UserState";

import { IRoutes } from "types/IRoutes";
import { IUser } from "types/IUser";

import { BottomMenu } from "./components/BottomMenu";
import { Header } from "./components/Header";

export const Main = () => {
  // Hooks
  const { storeHash } = useParams();
  const { setCurrentStore } = useCurrentStore();
  const userStoreHash = useUserAuthenticatedStoreHash();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();

  const { isInstallable, promptInstall } = usePromptInstall();
  const [openDialog, setOpenDialog] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Fns
  const removeAuthData = useCallback(() => {
    UserState.update((state) => {
      state.accessToken = "";
      state.user = {} as IUser;
      state.storeHash = "";
    });
  }, []);

  const handleDidMount = useCallback(async () => {
    try {
      const { storeConfig } = await getStoreConfig();

      if (storeConfig?.imageUrl !== "") {
        setFavicon(storeConfig.imageUrl);
        setAppleTouchIcon(storeConfig.imageUrl);
        StoreState.update((state) => {
          state.logoUrl = storeConfig.imageUrl;
        });
      }
    } catch (error) {
      handleErrors(error);
    }
  }, []);

  // Effects
  useEffectOnce(() => {
    handleDidMount();
  });

  useEffect(() => {
    if (isInstallable && mounted) {
      setOpenDialog(true);
    }
  }, [isInstallable, mounted]);

  useEffectOnce(() => {
    const store = stores.find((item) => item.storeHash === storeHash);

    if (!store) {
      navigate(IRoutes.NOT_FOUND, { replace: true });
      return;
    }

    document.title = store.name;
    setCurrentStore(store);
    addManifest(store);
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register(`/sw.js`, {
        scope: `/${store.storeHash}`,
      });
    }

    if (isAuthenticated && userStoreHash !== storeHash) removeAuthData();

    navigate(IRoutes.HOME);

    if (store.oneSignalAppId && store.oneSignalAppId !== "") {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(async function (OneSignal) {
        await OneSignal.init({
          appId: store.oneSignalAppId,
        });
      });

      OneSignal.init({
        appId: store.oneSignalAppId,
        autoRegister: true,
        allowLocalhostAsSecureOrigin: true,
        autoResubscribe: true,
      });
    }

    setMounted(true);
  });

  // Returns
  return (
    <>
      <Header />

      <Container sx={{ pt: 12 }}>
        <Outlet />
        <Box sx={{ height: 56, mt: 2 }} />
      </Container>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Instale nosso app</DialogTitle>
        <DialogContent>
          <p>Para facilitar o acesso, instale nosso app em seu dispositivo.</p>
          <Button
            onClick={() => {
              promptInstall();
              setOpenDialog(false);
            }}
          >
            Instalar
          </Button>
          <Button onClick={() => setOpenDialog(false)}>Agora não</Button>
        </DialogContent>
      </Dialog>

      <BottomMenu />
    </>
  );
};
